import { DatePicker, Form, Input, InputNumber } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import CalendarIcon from 'assets/icons/calendar';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormSection from 'components/ui/form-section';
import { SortedSelect } from 'components/ui/sorted-select';
import { EQueryKeys } from 'constants/query-keys';
import dayjs from 'dayjs';
import { useFileSession } from 'hooks/useFileSession';
import {
  ContractAdjustmentPayloadDtoAdjustmentTypeEnum,
  CreateUploadedFilePayloadDtoEntityTypeEnum,
} from 'libs/pinnacle-sdk';
import { noop } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';
import { contractApi } from 'services/apis';
import { getContractStatusLabel, getLocalDateForMidnightUTC } from 'utils';
import { formatLocaleNumber } from 'utils/format';
import BinderScheduleSection from '../binding/contract-detail/binder-schedule';
import {
  BinderScheduleItem,
  calculateBinderSchedule,
  EBinderScheduleKey,
} from '../binding/contract-detail/binder-schedule/form';
import FileUploader from '../file-uploader';
import SubmissionCurrencyTab from '../submission-currency-tabs';
import { DEFAULT_CONVERSION_CURRENCY } from '../submission-details/types';

const ContractAdjustmentDetail: React.FC = () => {
  const { t } = useTranslation();
  const { adjustmentId, contractId } = useParams<{
    contractId: string;
    adjustmentId: string;
  }>();
  const [form] = Form.useForm();

  const { data: contractAdjustment } = useQuery({
    queryKey: [EQueryKeys.GET_CONTRACT_ADJUSTMENT_DETAILS, adjustmentId],
    queryFn: () =>
      contractApi.contractGetAdjustmentDetail({
        contractAdjustmentId: adjustmentId || '',
      }),
    enabled: !!adjustmentId,
    refetchOnWindowFocus: false,
  });

  const { data: contract } = useQuery({
    queryKey: [EQueryKeys.CONTRACT_BY_ID, contractId],
    queryFn: () =>
      contractApi.getContractById({
        contractId: contractId || '',
        includeAdjustmentCount: true,
        applyAdjustments: true,
      }),
    enabled: !!contractId,
    refetchOnWindowFocus: false,
  });

  const [activeTab, setActiveTab] = useState<string>(
    DEFAULT_CONVERSION_CURRENCY
  );
  useEffect(() => {
    if (contract?.submission?.originalCurrency) {
      setActiveTab(contract?.submission?.originalCurrency);
    }
  }, [contract?.submission?.originalCurrency]);

  const { fileSessionId, fileList, setFileList } = useFileSession(
    contractAdjustment?.files
  );

  /**
   * set initial form values
   */
  useEffect(() => {
    if (contractAdjustment && contract) {
      form.setFieldsValue({
        transactionDate: dayjs(contractAdjustment.transactionDate),
        adjustmentType: contractAdjustment.adjustmentType,
        documentNumber: contractAdjustment.documentNo,
        pinnacleReferenceNumber: contract.pinnacleReferenceNumber,
        brokerReference: contractAdjustment.brokerReference,
        insured: contract.submission?.insured?.name,
        inceptionDate: contractAdjustment.inceptionDate
          ? getLocalDateForMidnightUTC(contractAdjustment.inceptionDate)
          : undefined,
        expiryDate: contractAdjustment.expiryDate
          ? getLocalDateForMidnightUTC(contractAdjustment.expiryDate)
          : undefined,
        premiumPaymentWarranty: contractAdjustment.premiumPaymentWarranty,
        paymentWarrantyDate: dayjs(contractAdjustment.ppwDueDate),
        underwritingComments: contractAdjustment.underwritingComments,
        totalInsurableValue: contractAdjustment.tiv,
        layerPremiumAdjustmentAmount:
          contractAdjustment.premiumAdjustmentAmount,
      });
    }
  }, [contractAdjustment, contract]);

  const binderScheduleData = useMemo<BinderScheduleItem[]>(() => {
    const rateData =
      contractAdjustment?.adjustmentType ===
      ContractAdjustmentPayloadDtoAdjustmentTypeEnum.RateUpdate
        ? contractAdjustment
        : contract;
    return calculateBinderSchedule(
      [
        {
          key: EBinderScheduleKey.CEDING,
          label: t('contractDetail.cedingCommission'),
          rate: rateData?.cedingCommissionRate
            ? Number(rateData?.cedingCommissionRate) * 100
            : undefined,
          isNegative: true,
        },
        {
          key: EBinderScheduleKey.BROKERAGE,
          label: t('contractDetail.sourceBrokerage'),
          rate: rateData?.sourceBrokerageRate
            ? Number(rateData?.sourceBrokerageRate) * 100
            : undefined,
          isNegative: true,
        },
        {
          key: EBinderScheduleKey.ERE,
          label: t('contractDetail.ere'),
          rate: rateData?.ereRate ? Number(rateData?.ereRate) * 100 : undefined,
          isNegative: true,
        },
        {
          key: EBinderScheduleKey.WITHHOLDING_TAX,
          label: t('contractDetail.withholdingTax'),
          rate: rateData?.withholdingTaxRate
            ? Number(rateData?.withholdingTaxRate) * 100
            : undefined,
          isNegative: true,
        },
        {
          key: EBinderScheduleKey.FEES,
          label: t('contractDetail.taxesAndFees'),
          amount: rateData?.taxAndFeeAmount
            ? Number(rateData?.taxAndFeeAmount)
            : undefined,
          isNegative: true,
        },
        {
          key: EBinderScheduleKey.AIT,
          label: t('contractDetail.ait'),
          rate: rateData?.aitRate ? Number(rateData?.aitRate) * 100 : undefined,
          isNegative: true,
        },
        {
          key: EBinderScheduleKey.GST_CEDING,
          label: t('contractDetail.gstCeding'),
          rate: rateData?.gstRateOnCedingCommission
            ? Number(rateData.gstRateOnCedingCommission) * 100
            : undefined,
          isNegative: true,
        },
        {
          key: EBinderScheduleKey.GST_BROKERAGE,
          label: t('contractDetail.gstSourceBrokerage'),
          rate: rateData?.gstRateOnSourceBrokerage
            ? Number(rateData?.gstRateOnSourceBrokerage) * 100
            : undefined,
          isNegative: true,
        },
        {
          key: EBinderScheduleKey.GST_PREMIUM,
          label: t('contractDetail.gstPremium'),
          rate: rateData?.gstRateOnPremium
            ? Number(rateData?.gstRateOnPremium) * 100
            : undefined,
          isNegative: true,
        },
      ],
      {
        premium:
          contractAdjustment?.adjustmentType ===
          ContractAdjustmentPayloadDtoAdjustmentTypeEnum.RateUpdate
            ? Number(contractAdjustment?.layerPremium)
            : Number(contractAdjustment?.premiumAdjustmentAmount),
        binderShareSigned:
          Number(contractAdjustment?.binderShareSigned) * 100 || 0,
        brokerCountry: contract?.broker?.country,
        layerTotalDeductions: contract?.layer?.totalDeductions,
        binderPinCommission: contract?.binder?.pinCommission,
      },
      t,
      false
    );
  }, [contract, contractAdjustment, t]);

  return (
    <div className="my-4 rounded-2xl bg-[#E3F2FB] p-3">
      <div className="rounded-lg bg-[#E3F2FB]">
        <div className="rounded-lg px-2 bg-white py-4 space-y-4">
          <h1 className="text-2xl font-semibold px-2">
            {t('contractAdjustment.contractAdjustment')}
          </h1>
          <Form form={form} layout="vertical" disabled={true} preserve={true}>
            <FormSection
              title={t(
                'contractAdjustment.transactionDetailsAndClassification'
              )}
              collapsible
            >
              <FormLayoutGroup mdColumns={3}>
                <Form.Item
                  label={t('contractAdjustment.effectiveDate')}
                  name="transactionDate"
                >
                  <DatePicker
                    size="large"
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    suffixIcon={<CalendarIcon />}
                  />
                </Form.Item>

                <Form.Item
                  label={t('contractAdjustment.adjustmentType')}
                  name="adjustmentType"
                >
                  <SortedSelect
                    size="large"
                    showSearch
                    placeholder={t('general.selectField', {
                      field: t('contractAdjustment.adjustmentType'),
                    })}
                    suffixIcon={<ArrowDownIcon />}
                    options={Object.values(
                      ContractAdjustmentPayloadDtoAdjustmentTypeEnum
                    ).map((type) => ({
                      value: type,
                      label: getContractStatusLabel(t)(type),
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  label={t('contractAdjustment.documentNumber')}
                  name="documentNumber"
                >
                  <Input disabled size="large" />
                </Form.Item>
              </FormLayoutGroup>

              <FormLayoutGroup mdColumns={3}>
                <Form.Item
                  label={t('contractAdjustment.pinnacleReferenceNumber')}
                  name="pinnacleReferenceNumber"
                >
                  <Input disabled size="large" />
                </Form.Item>

                <Form.Item
                  label={t('contractAdjustment.brokerReference')}
                  name="brokerReference"
                >
                  <Input size="large" />
                </Form.Item>

                <Form.Item
                  label={t('contractAdjustment.insured')}
                  name="insured"
                >
                  <Input disabled size="large" />
                </Form.Item>
              </FormLayoutGroup>

              <FormLayoutGroup mdColumns={2}>
                <Form.Item
                  label={t('contractAdjustment.inceptionDate')}
                  name="inceptionDate"
                >
                  <DatePicker
                    size="large"
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    suffixIcon={<CalendarIcon />}
                  />
                </Form.Item>

                <Form.Item
                  label={t('contractAdjustment.expiryDate')}
                  name="expiryDate"
                >
                  <DatePicker
                    size="large"
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    suffixIcon={<CalendarIcon />}
                  />
                </Form.Item>
              </FormLayoutGroup>

              <FormLayoutGroup mdColumns={2}>
                <Form.Item
                  label={t('contractAdjustment.premiumPaymentWarranty')}
                  name="premiumPaymentWarranty"
                >
                  <InputNumber<number>
                    size="large"
                    className="w-full"
                    precision={0}
                    min={1}
                    formatter={formatLocaleNumber}
                    placeholder={t('general.enter', {
                      field: t('contractAdjustment.premiumPaymentWarranty'),
                    })}
                  />
                </Form.Item>

                <Form.Item
                  label={t('contractAdjustment.paymentWarrantyDate')}
                  name="paymentWarrantyDate"
                >
                  <DatePicker
                    disabled
                    placeholder={t('general.selectField', {
                      field: t('contractAdjustment.paymentWarrantyDate'),
                    })}
                    size="large"
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    suffixIcon={<CalendarIcon />}
                  />
                </Form.Item>
              </FormLayoutGroup>

              <FormLayoutGroup mdColumns={1}>
                <Form.Item
                  name="underwritingComments"
                  label={t('contractAdjustment.underwritingComments')}
                >
                  <TextArea
                    rows={4}
                    placeholder={t('contractAdjustment.addYourReasonHere')}
                  ></TextArea>
                </Form.Item>
              </FormLayoutGroup>
            </FormSection>

            <FormSection
              title={t('contractAdjustment.midTermAdjustments')}
              collapsible
            >
              <FormLayoutGroup mdColumns={2}>
                <Form.Item
                  name="totalInsurableValue"
                  label={t('contractAdjustment.totalInsurableValue')}
                >
                  <InputNumber<number>
                    size="large"
                    className="w-full"
                    min={0}
                    precision={2}
                    formatter={formatLocaleNumber}
                    placeholder={t('general.enter', {
                      field: t('contractAdjustment.totalInsurableValue'),
                    })}
                  />
                </Form.Item>
                <Form.Item
                  name="layerPremiumAdjustmentAmount"
                  label={t(
                    'contractAdjustment.layerPremiumAdjustmentAmountProrated'
                  )}
                >
                  <InputNumber<number>
                    size="large"
                    className="w-full"
                    precision={2}
                    formatter={formatLocaleNumber}
                    placeholder={t('general.enter', {
                      field: t(
                        'contractAdjustment.layerPremiumAdjustmentValue'
                      ),
                    })}
                  />
                </Form.Item>
              </FormLayoutGroup>

              <SubmissionCurrencyTab
                originalCurrency={
                  contract?.submission?.originalCurrency ||
                  DEFAULT_CONVERSION_CURRENCY
                }
                activeTab={activeTab}
                onTabChange={setActiveTab}
                className="custom-tabs-with-bottom-curve-white"
                tabPosition="right"
              />

              <BinderScheduleSection
                currency={activeTab}
                originalCurrency={contract?.submission?.originalCurrency}
                // layerPremium is not used in view only mode
                layerPremium={0}
                setBinderScheduleData={noop}
                binderScheduleData={binderScheduleData}
                binderShareSigned={Number(
                  contractAdjustment?.binderShareSigned
                )}
                brokerCountry={contract?.broker?.country}
                layerTotalDeductions={contract?.layer?.totalDeductions}
                binderPinCommission={contract?.binder?.pinCommission}
                contractCurrencyRate={
                  contract?.rateToDefaultCurrency
                    ? Number(contract.rateToDefaultCurrency)
                    : 1
                }
                setIsBinderScheduleValid={noop}
                allowPremiumEdit={false}
                disabled={true}
              />
            </FormSection>

            <FormSection
              title={t('contractAdjustment.uploadFiles')}
              collapsible
            >
              <FormLayoutGroup mdColumns={1}>
                <Form.Item className="mt-6">
                  {fileSessionId && (
                    <FileUploader
                      sessionId={fileSessionId}
                      entityType={
                        CreateUploadedFilePayloadDtoEntityTypeEnum.ContractAdjustment
                      }
                      fileList={fileList}
                      setFileList={setFileList}
                    />
                  )}
                </Form.Item>
              </FormLayoutGroup>
            </FormSection>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ContractAdjustmentDetail;
